import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import axios from 'axios';

// helpers
import { prepareSelectParam, prepareFilterParam, prepareSortParam } from '../lib/queryParams';
import { sortObject } from '/src/lib/helpers';

// constants
import { API_URL_NEW } from '../lib/endpoints'
import { ALLOWED_QUERY_OPERATOR, ENTITY_TYPE, FUNDING_STATUS, TAXONOMIES } from '../lib/constants';

const fundingApi = {

    getFundingRoundsData: (selectableFields = [], filters = '', limit, page, sort, signal) => {

        const fields = prepareSelectParam([
            'id', 'investedin_id', 'investedin_data',
            'undisclosed_amount',
            ...selectableFields
        ])
        const arrFilters = [
            { key: 'investedin_type_filter', value: [ENTITY_TYPE.startup], op: ALLOWED_QUERY_OPERATOR.equal },
            // include all funding stages and pe funding stages, except exits
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }
        ];

        // exclude draft rounds
        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({
                key: 'verification_status',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            })
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        const combinedFilters = filters ? `${filters}^,${permanentFilters}` : permanentFilters;
        const url = `${API_URL_NEW}/funding-breakdown?fields=${fields}&filters=${combinedFilters}&limit=${limit}&page=${page}&sort=${sort}`;

        return new Promise((resolve, reject) => {
            axios.get(url, { signal }).then((response) => {
                if (response.data.data && response.data.data.length > 0) {
                    resolve({
                        records: response.data.data,
                        count: response.data.navigation?.total || 0
                    });
                } else { resolve([]) }
            }).catch((err) => reject(err));
        });
    },

    getFundingBreakdownsCount: (fundingFilters = [], filters = '', signal) => {

        const arrFilters = [
            { key: 'investedin_type_filter', value: [ENTITY_TYPE.startup], op: ALLOWED_QUERY_OPERATOR.equal },
            // include all funding stages and pe funding stages, except exits
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }
        ];

        if (!fundingFilters.funding_verification_status) {
            arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        let filterStr = `filters=`;
        if (filters.finalFilters) {
            filterStr += `${filters.finalFilters}^,`;
        }
        filterStr += `${permanentFilters}`;

        const url = `${API_URL_NEW}/funding-breakdown/setting/count?${filterStr}`;

        return new Promise((resolve, reject) => {

            axios.get(url, { signal }).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        count: response.data.data
                    });
                } else {
                    resolve({});
                }
            }).catch((err) => reject(err));
        });
    },

    // Company Directory Transactions View - funding rounds raised by startups & corporates
    getCompanyFundingRoundsData: (selectableFields = [], filters = '', limit, page, sort, signal) => {

        const fields = prepareSelectParam([
            'id', 'investedin_id', 'investedin_data',
            'undisclosed_amount',
            ...selectableFields
        ])
        const arrFilters = [
            // include all funding stages and pe funding stages, except exits
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }
        ];

        // add investedin_type_filter if the user hasn't already picked a Company Type (Startup/Corporate)
        if (!filters?.includes('investedin_type_filter')) {
            arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate], op: ALLOWED_QUERY_OPERATOR.equal });
        }

        // exclude draft rounds
        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({
                key: 'verification_status',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            })
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        const combinedFilters = filters ? `${filters}^,${permanentFilters}` : permanentFilters;
        const url = `${API_URL_NEW}/companies-funding?fields=${fields}&filters=${combinedFilters}&limit=${limit}&page=${page}&sort=${sort}`;

        return new Promise((resolve, reject) => {
            axios.get(url, { signal }).then((response) => {
                if (response.data.data && response.data.data.length > 0) {
                    resolve({
                        records: response.data.data,
                        count: response.data.navigation?.total || 0
                    });
                } else { resolve([]) }
            }).catch((err) => reject(err));
        });
    },

    getCompanyFundingBreakdownsCount: (fundingFilters = [], filters = '', signal) => {

        const arrFilters = [
            // include all funding stages and pe funding stages, except exits
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }
        ];

        // add investedin_type_filter if the user hasn't already picked a Company Type (Startup/Corporate)
        if (!filters?.finalFilters?.includes('investedin_type_filter')) {
            arrFilters.push({ key: 'investedin_type_filter', value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate], op: ALLOWED_QUERY_OPERATOR.equal });
        }

        if (!fundingFilters.funding_verification_status) {
            arrFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        let filterStr = `filters=`;
        if (filters.finalFilters) {
            filterStr += `${filters.finalFilters}^,`;
        }
        filterStr += `${permanentFilters}`;

        const url = `${API_URL_NEW}/companies-funding/setting/count?${filterStr}`;

        return new Promise((resolve, reject) => {

            axios.get(url, { signal }).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        count: response.data.data
                    });
                } else {
                    resolve({});
                }
            }).catch((err) => reject(err));
        });
    },

    getDealsCount: (id, entityType, getFilters = []) => {
        getFilters.push(
            { key: 'investedin_type_filter', value: [entityType.toUpperCase()], op: ALLOWED_QUERY_OPERATOR.equal },
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal },
            { key: 'investedin_filter', value: [id], op: ALLOWED_QUERY_OPERATOR.equal }
        );

        if (!getFilters.funding_verification_status) {
            getFilters.push({ key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        const permanentFilters = prepareFilterParam(getFilters);
        const url = `${API_URL_NEW}/funding-breakdown/setting/count?filters=${permanentFilters}`;

        return new Promise((resolve, reject) => {

            axios.get(url).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        count: response.data.data
                    });
                } else {
                    resolve({});
                }
            }).catch((err) => reject(err));
        });
    },

    getFundingData: (id, entityType, recordsToLoad, getFilters = [], pathName, page, sectionType = "", sortConfig = null) => {
        return new Promise((resolve, reject) => {

            if (entityType === "startup" || sectionType == "directory" || (entityType === "corporate" && sectionType === "fundingRounds")) {

                let arrFilters = [];

                //case 1: no filters, or verification filter is missing - add mandatory filter to exclude draft funding rounds, draft: verification_status = 3
                //case 2: use user specified filters
                if (getFilters.length === 0 || getFilters.findIndex(filter => filter.key === 'verification_status_filter') === -1) {
                    arrFilters.push({
                        key: 'verification_status_filter',
                        value: [FUNDING_STATUS.draft],
                        op: ALLOWED_QUERY_OPERATOR.notEq
                    });
                } else {
                    arrFilters = [...getFilters];
                }

                let fieldsList = ['id', 'amount_raised', 'round_amount_raised', 'funding_stage_data', 'investedin_data', 'investors_data', 'funding_breakdowns_data', 'post_money_valuation', 'valuation'];

                // removing  post_money_valuation  field if page is single startup profile
                if (pathName && pathName.includes("startups/")) {
                    fieldsList = fieldsList.filter(field => field !== 'post_money_valuation');
                }

                const filters = prepareFilterParam(arrFilters);

                const fields = prepareSelectParam(fieldsList);

                const sortParam = (sortConfig && sortConfig.key && sortConfig.dir)
                    ? `${sortConfig.key}^:${sortConfig.dir.toUpperCase()}`
                    : 'date_raised^:DESC';
                let url = `${API_URL_NEW}/${entityType}/${id}/funding?fields=${fields}&filters=${filters}&limit=${recordsToLoad}&sort=${sortParam}`;
                if (page) {
                    url += `&page=${page}`;
                }

                axios.get(url).then(response => {

                    let resp = response.data.data;

                    if (resp && resp.length > 0) {

                        // exclude invalid test data
                        let rounds = [];
                        for (let i = 0; i < resp.length; i++) {
                            if (resp[i].investedin_data && resp[i].funding_breakdowns_data.length > 0) {
                                resp[i].funding_breakdowns_data = sortObject(
                                    resp[i].funding_breakdowns_data, "date_raised"
                                )
                                resp[i].date_raised = resp[i].funding_breakdowns_data[0].date_raised
                                rounds.push(resp[i])
                            }
                        }

                        let latestStage = 'N/A';
                        if (!isEmpty(resp[0].funding_stage_data)) {
                            latestStage = resp[0].funding_stage_data.name;
                        }

                        resolve({
                            rounds,
                            latestStage,
                            totalRecords: response.data.navigation?.total || 0,
                        });

                    } else {

                        resolve([])

                    }

                }).catch(err => reject(err))

            } else if ((entityType === "user") || (entityType === "investor") || (entityType === "enabler") || (entityType === "corporate")) {

                const investedFields = prepareSelectParam([
                    'funding_id',
                    'funding_stage_data',
                    'invested_amount',
                    'investedin_data',
                    'total_amount',
                    'date_raised',
                    'investors_data',
                    'created_at',
                    'verification_status',
                    'funding_breakdowns_data',
                    'valuation',
                    'fund_manager_data',
                    'follow_on_count'
                ]);

                // add mandatory filter to exclude draft funding rounds, draft: verification_status = 3
                const arrFilters = [...getFilters, { key: 'verification_status_filter', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }];
                const filters = prepareFilterParam(arrFilters);

                // build sort param — use caller-supplied sortConfig if provided, otherwise fall back to default
                const sortParams = (sortConfig && sortConfig.key && sortConfig.dir)
                    ? [{ key: sortConfig.key, sortVal: sortConfig.dir }, { key: 'id', sortVal: 'desc' }]
                    : [{ key: 'date_raised', sortVal: 'desc' }, { key: 'id', sortVal: 'desc' }];
                const sort = prepareSortParam(sortParams);
                let url = `${API_URL_NEW}/${entityType}/${id}/invested/?fields=${investedFields}&filters=${filters}&limit=${recordsToLoad}&sort=${sort}`;
                if (page) {
                    url += `&page=${page}`;
                }

                axios.get(url).then(response => {

                    let rounds = response.data.data

                    // ignore invalid test data
                    let cleanRounds = [];
                    rounds.map((item, i) => {
                        if (item.investedin_data) {
                            cleanRounds.push(item);
                        }
                    })

                    resolve({
                        rounds: cleanRounds,
                        totalRecords: response.data.navigation?.total || 0
                    });

                }).catch(err => reject(err))

            }

        })
    },

    // used to check if funding round exists for current filters
    getFundingRoundData: (startupId, stage) => {
        const fields = prepareSelectParam(["id", "entity_id", "entity_type", "amount_raised", "post_money_valuation", 'funding_breakdowns_data', 'investors_data'])
        const filters = prepareFilterParam([{ key: 'investedin_id', value: [startupId], op: ALLOWED_QUERY_OPERATOR.equal }, { key: 'funding_stage_id', value: [stage], op: ALLOWED_QUERY_OPERATOR.equal }])

        const url = `${API_URL_NEW}/funding?fields=${fields}&filters=${filters}&limit=1`;

        return new Promise((resolve, reject) => {
            axios.get(url).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        fundingRound: response.data.data,
                    });
                } else {
                    resolve([]);
                }
            }).catch((err) => reject(err));
        });
    },

    checkForDuplication: (startupId, stage) => {

        const filters = prepareFilterParam([
            {
                key: 'investedin_id',
                value: [startupId],
                op: ALLOWED_QUERY_OPERATOR.equal
            },
            {
                key: 'funding_stage_id',
                value: [stage],
                op: ALLOWED_QUERY_OPERATOR.equal
            },
            {
                key: 'is_deleted',
                value: [0],
                op: ALLOWED_QUERY_OPERATOR.equal
            },
            {
                key: 'verification_status_filter',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            }
        ]);

        const url = `${API_URL_NEW}/funding/setting/count?filters=${filters}`;

        return new Promise((resolve, reject) => {
            axios.get(url).then(async (response) => {
                resolve(response.data.data);
            }).catch(err => {
                let data = {
                    error: err,
                    // set static value in case of error
                    totalCount: "2,700"
                }
                console.log("ERROR: ", err);
                reject(data);
            })
        })
    },

    // get total funding rounds including pe
    getTotalFundingRoundsCount: () => {
        //  add mandatory filter to exclude draft funding rounds, draft: verification_status = 3
        const filters = prepareFilterParam([
            { key: 'verification_status', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq },
            // funding stages including pe
            { key: 'funding_stage_type_filter', value: [TAXONOMIES.fundingStage, TAXONOMIES.peFundingStage], op: ALLOWED_QUERY_OPERATOR.equal }
        ]);

        const url = `${API_URL_NEW}/funding/setting/count?filters=${filters}`;

        return new Promise((resolve, reject) => {
            axios.get(url).then(async (response) => {
                if (response.data.status === 200) {
                    resolve(response.data.data);
                } else {
                    let error = new Error(response);
                    throw error;
                }
            }).catch(err => {
                let data = {
                    error: err,
                    // set static value in case of error
                    totalCount: "10,000"
                }
                console.log("ERROR: ", err);
                reject(data);
            })
        })
    },

    getLPFundingRoundsCount: (filters = '') => {
        const filterStr = typeof filters === 'string' ? filters : '';
        const arrFilters = [];
        if (filterStr.indexOf('verification_status_filter') === -1) {
            arrFilters.push({ key: 'verification_status_filter', value: [FUNDING_STATUS.draft], op: ALLOWED_QUERY_OPERATOR.notEq }) // exclude draft funding rounds
        }

        const permanentFilters = prepareFilterParam(arrFilters);
        const combinedFilters = filterStr.length > 0 ? `${filterStr}^,${permanentFilters}` : permanentFilters;
        const url = `${API_URL_NEW}/fund/setting/count?filters=${combinedFilters}`;

        return new Promise((resolve, reject) => {

            axios.get(url).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        count: response.data.data
                    });
                } else {
                    resolve({});
                }
            }).catch((err) => reject(err));
        });
    },

    getRecentFundingRoundsData: (selectableFields = [], filters = '', limit, page, sort) => {
        const fields = prepareSelectParam(selectableFields);

        const arrFilters = [
            { key: 'investedin_type_filter', value: ['startup'], op: ALLOWED_QUERY_OPERATOR.equal }
        ];

        // exclude draft rounds
        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({
                key: 'verification_status',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            })
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        const combinedFilters = filters ? `${filters}^,${permanentFilters}` : permanentFilters;
        const url = `${API_URL_NEW}/funding-breakdown/recent-funding?fields=${fields}&filters=${combinedFilters}&limit=${limit}&page=${page}&sort=${sort}`;

        return new Promise((resolve, reject) => {
            axios.get(url).then((response) => {
                const resp = response.data.data;
                resolve({
                    records: resp,
                    count: response.data.navigation?.total || 0
                });
            }).catch((err) => reject(err));
        });
    },

    getPrivateEquityRounds: (selectableFields = [], filters = '', limit, page, sort, signal) => {

        const fields = prepareSelectParam([
            'id', 'investedin_id', 'investedin_data',
            'funding_stage_data', // required for InvestorDetailModal in transactions view to show stage name
            ...selectableFields
        ])

        const arrFilters = [
            {
                key: 'funding_stage_type_filter',
                value: [TAXONOMIES.peFundingStage],
                op: ALLOWED_QUERY_OPERATOR.equal
            }
        ];

        // exclude draft rounds
        if (filters.indexOf('verification_status') === -1) {
            arrFilters.push({
                key: 'verification_status',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            })
        }

        // Fetch private equity investments in startups and corporates
        if (filters.indexOf('investedin_type_filter') === -1) {
            arrFilters.push({
                key: 'investedin_type_filter',
                value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate],
                op: ALLOWED_QUERY_OPERATOR.equal
            })
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        let filterStr = filters ? `${filters}^,${permanentFilters}` : `${permanentFilters}`;

        const url = `${API_URL_NEW}/funding-breakdown?fields=${fields}&filters=${filterStr}&limit=${limit}&page=${page}&sort=${sort}`;

        return new Promise((resolve, reject) => {
            axios.get(url, { signal }).then((response) => {
                if (response.data.data && response.data.data.length > 0) {
                    resolve({
                        records: response.data.data,
                        count: response.data.navigation?.total || 0
                    });
                } else { resolve([]) }
            }).catch((err) => reject(err));
        });
    },

    getPrivateEquityCount: (fundingFilters = [], filters = '', signal) => {

        const arrFilters = [
            {
                key: 'funding_stage_type_filter',
                value: [TAXONOMIES.peFundingStage],
                op: ALLOWED_QUERY_OPERATOR.equal
            }
        ];

        // exclude draft funding rounds
        if (!fundingFilters.funding_verification_status) {
            arrFilters.push({
                key: 'verification_status',
                value: [FUNDING_STATUS.draft],
                op: ALLOWED_QUERY_OPERATOR.notEq
            })
        }

        // Fetch private equity investments in startups and corporates
        if (!fundingFilters.entity_type) {
            arrFilters.push({
                key: 'investedin_type_filter',
                value: [ENTITY_TYPE.startup, ENTITY_TYPE.corporate],
                op: ALLOWED_QUERY_OPERATOR.equal
            })
        }

        const permanentFilters = prepareFilterParam(arrFilters);

        let filterStr = filters.finalFilters ?
            `filters=${filters.finalFilters}^,${permanentFilters}`
            : `filters=${permanentFilters}`;

        const url = `${API_URL_NEW}/funding-breakdown/setting/count?${filterStr}`;

        return new Promise((resolve, reject) => {

            axios.get(url, { signal }).then((response) => {
                if (response.data.status === 200) {
                    resolve({
                        count: response.data.data
                    });
                } else {
                    resolve({});
                }
            }).catch((err) => reject(err));
        });
    },

}

export default fundingApi;
